import type { ContentSegment, VariableDefinition } from './types';
import { valueForDisplay } from './utils';

const VARIABLE_REGEX = /(\{\{[\w_]+\}\})/g;
const BLOCK_ELEMENT_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DIV',
  'DL',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'UL',
]);

/**
 * Parses content string into segments of text and variables
 */
export const parseContent = (content: string): ContentSegment[] => {
  return content
    .split(VARIABLE_REGEX)
    .filter(Boolean) // Remove empty parts produced by .split()
    .map((part): ContentSegment => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return {
          type: 'variable',
          content: part,
          varName: part.slice(2, -2),
        };
      }
      return { type: 'text', content: part };
    });
};

/**
 * Gets the current value for a variable, using the value from content or defaultValue
 */
export const getVariableValue = (
  varName: string,
  variableValues: Record<string, string>,
  variableDefinitions: VariableDefinition[],
): string => {
  // If the user has already set a value for the variable, use that
  if (variableValues[varName]) {
    return variableValues[varName];
  }

  // If the user hasn't set a value for the variable, use the default value
  const definition = variableDefinitions.find((d) => d.varName === varName);
  // If no definition or default, return the variable name itself (e.g., "patient_name")
  return definition?.defaultValue ?? varName;
};

/**
 * Renders a template string by replacing {{variable}} placeholders with values from a map.
 * Shared utility for rendering templates with variable values.
 */
export const renderTemplate = (
  template: string,
  variableValues: Record<string, string>,
  fallbackValue = '___',
) => {
  if (!template) return template;

  return template.replace(/\{\{([\w_]+)\}\}/g, (match, varName) => {
    const raw = variableValues[varName];
    return raw != null ? valueForDisplay(raw) : fallbackValue;
  });
};

/**
 * Serializes the contentEditable DOM back to a string with {{variable}} syntax.
 * Manually traverses the DOM to preserve whitespace (innerText on detached elements
 * doesn't apply CSS white-space rules, causing spaces to collapse).
 */
export const serializeContent = (container: HTMLDivElement | null): string => {
  if (!container) return '';

  const result: string[] = [];

  const endsWithNewline = () => result[result.length - 1] === '\n';
  const appendNewline = () => {
    if (!endsWithNewline()) {
      result.push('\n');
    }
  };
  const isBlockElement = (node: Node): node is HTMLElement =>
    node.nodeType === Node.ELEMENT_NODE &&
    BLOCK_ELEMENT_TAGS.has((node as HTMLElement).tagName);
  const hasMeaningfulFollowingSibling = (node: Node) => {
    let sibling = node.nextSibling;

    while (sibling) {
      if (sibling.nodeType === Node.TEXT_NODE) {
        if (sibling.textContent?.trim()) {
          return true;
        }
      } else if (sibling.nodeType === Node.ELEMENT_NODE) {
        return true;
      }
      sibling = sibling.nextSibling;
    }

    return false;
  };

  const processNode = (node: Node): void => {
    // Text nodes: preserve text content exactly as-is
    if (node.nodeType === Node.TEXT_NODE) {
      result.push(node.textContent || '');
      return;
    }

    const el = node as HTMLElement;

    // Variable chips: convert back to {{variableName}} syntax
    if (el.dataset.variable) {
      result.push(`{{${el.dataset.variable}}}`);
      return;
    }

    // Line breaks: convert <br> to newline character
    if (el.tagName === 'BR') {
      result.push('\n');
      return;
    }

    if (isBlockElement(node) && result.length > 0) {
      appendNewline();
    }

    // Recursively process all child nodes
    Array.from(el.childNodes).forEach(processNode);

    if (isBlockElement(node) && hasMeaningfulFollowingSibling(node)) {
      appendNewline();
    }
  };

  // Process all top-level children of the container
  Array.from(container.childNodes).forEach(processNode);

  // Join all collected parts into final string
  return result.join('');
};

export { valueForDisplay, normalizeMultiValueDisplay } from './utils';
